import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  useMediaQuery,
  TableCell,
  Table,
  TableHead,
  Box,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { NoData } from "./noData";
import { Chart } from "../components/chart";
import { getDataSingleDay, getDataWeekOrMonth } from "./BarGraphUtils";
import useAuth from "../hooks/useAuth";
import { filterSingleDay, filterWeek } from "../pages/Home/FilterInfoOverview";
import { formatEurPrice } from "../utils/currencyUtil";
import * as PropTypes from "prop-types";
import moment from "moment";

export const staffMode = "staff";
export const productMode = "product";

function TableRow(props) {
  return null;
}

TableRow.propTypes = {children: PropTypes.node};

function BarTurnover({
                       displayMode,
                       title,
                       product,
                       additionalFilters,
                       transformer,
                       serieBuiler,
                       transformerSumUpDays,
                       serieBuilerSumUpDay,
                       noPrice
                     }) {
  const {
    orders,
    currentEstablishment,
    sumUpDays,
    filter,
    categories,
    staffs,
    paymentModes,
    locations,
    products,
    weatherSingleDay,
    weatherDays
  } = useAuth();
  const theme = useTheme();

  // Hook MUI pour savoir si l'écran est >= 'lg'
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const [chartOptions, setChartOptions] = useState(null);
  const [loadingData, setLoadingData] = useState(false);


  useEffect(() => {
    // Lorsque toutes les données (orders, products, etc.) sont chargées,
    // on construit le dataSource
    if (
        (!orders && !sumUpDays) ||
        !products ||
        !categories ||
        !staffs ||
        !paymentModes ||
        !locations
    ) {
      return;
    }
    const turnoverData = getTurnoverOrQteData(additionalFilters, transformer, transformerSumUpDays, serieBuilerSumUpDay, noPrice);
    if (turnoverData) {
      setOptionsWithDataSource(turnoverData);
    } else {
      setChartOptions(null);
    }
  }, [orders, products, categories, staffs, paymentModes, locations, sumUpDays]);


  function getTurnoverOrQteData(additionalFilters, transformer, transformerSumUpDays, serieBuilerSumUpDay, noPrice) {
    // Gère le calcul du dataSource selon le type de filtre
    if (orders && filter?.filterType === filterSingleDay && products && categories) {
      return getDataSingleDay(
          orders,
          displayMode,
          products,
          categories,
          product,
          currentEstablishment()?.timeZone,
          additionalFilters,
          transformer,
          serieBuiler,
          noPrice

      );
    }
    if (
        filter?.filterType !== filterSingleDay &&
        categories &&
        products &&
        staffs &&
        paymentModes &&
        locations
    ) {
      return getDataWeekOrMonth(
          sumUpDays,
          displayMode,
          products,
          staffs,
          paymentModes,
          locations,
          categories,
          product,
          currentEstablishment()?.timeZone,
          additionalFilters,
          transformer,
          transformerSumUpDays,
          serieBuilerSumUpDay,
          filter?.filterType,
          noPrice

      );
    }
    return null;
  }

  function setOptionsWithDataSource(dataSource) {
    // Config de base (plutôt pour desktop)
    let newOptions = {
      chart: {
        type: "bar",
        height: 350,
        stacked: true,
        toolbar: { show: true },
        zoom: { enabled: true },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 10,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          dataLabels: {
            // Label total en haut de chaque stack
            total: {
              enabled: true,
              style: {
                fontSize: "13px",
                fontWeight: 900,
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true, // Sous-labels activés
        formatter: (val) => noPrice ? val : formatEurPrice(val, !lgUp),
      },
      xaxis: {
        categories: dataSource.dates,
      },
      yaxis: {
        labels: {
          show: false,
          formatter: (val) => noPrice ? val : formatEurPrice(val, !lgUp),
        },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        offsetY: 10,
        fontSize: lgUp ? "12px" : "10px",
      },
      fill: { opacity: 1 },
    };

    // Si on est sur mobile (lgUp === false),
    // on surchage certaines options pour alléger l'affichage
    if (!lgUp) {
      newOptions = {
        ...newOptions,
        chart: {
          ...newOptions.chart,
          height: 320, // un peu plus petit
        },
        legend: {
          ...newOptions.legend,
          position: "bottom", // légende en bas
          fontSize: "10px",
        },
        dataLabels: {
          ...newOptions.dataLabels,
          enabled: false, // On masque les sous-labels
        },
        plotOptions: {
          ...newOptions.plotOptions,
          bar: {
            ...newOptions.plotOptions.bar,
            dataLabels: {
              total: {
                enabled: true, // On garde le total
                style: {
                  fontSize: "11px",
                  fontWeight: 700,
                },
              },
            },
          },
        },
        xaxis: {
          ...newOptions.xaxis,
          labels: {
            ...newOptions.xaxis.labels,
            rotate: -45,
            style: {
              fontSize: "9px",
            },
          },
        },
      };
    }

    setChartOptions({
      series: dataSource.series || [],
      options: newOptions,
    });
  }

  function getImage(index) {
    if (!weatherSingleDay) {
      return null;
    }
    const hour = chartOptions?.options?.xaxis?.categories[index];
    const detailItem = weatherSingleDay.hoursDetail.find(
        (hourDetail) =>  {
          const formatHour = moment(hourDetail.time).format("HH:mm");
          const detail = formatHour === hour;
          return detail
        });
    if (!detailItem) {
      return (
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}/>
      )
    }
    return (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}>
          <Box>
            <img
                src={detailItem.conditionIcon} width={'50px'}/>
          </Box>
          {lgUp &&
              <>
                <Box>
                  <Typography variant={"body2"} color="text.secondary">
                    {`🌡️ ${detailItem.temp_c} °C`}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant={"body2"} color="text.secondary">
                    {`💨️ ${detailItem.wind_kph} kh`}
                  </Typography>
                </Box>
              </>
          }
        </Box>
    )
  }

  function getWeatherImageDay(id) {
    const weatherDay = weatherDays.find((wd) => {
      return wd.id === id;
    });
    if (weatherDay) {
      return (

          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}>
            <Box>
              <img
                  src={weatherDay.conditionIcon} width={'50px'}/>
            </Box>
            {lgUp &&
                <>
                  <Box>
                    <Typography variant={"body2"} color="text.secondary">
                      {`🌡️ ${weatherDay.avgtemp_c} °C`}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={"body2"} color="text.secondary">
                      {`💨️ ${weatherDay.maxwind_kph} kh`}
                    </Typography>
                  </Box>
                </>
            }
          </Box>
          //
          // <p>{JSON.stringify(weatherDay)}</p>
      );
    }
    return(
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}/>
    );

  }

  return (
      <Card>
        <CardHeader title={title} />
        <CardContent sx={{ pt: 0 }}>
          {loadingData && <NoData />}
          {(orders && orders.length > 0) || (sumUpDays && sumUpDays.length > 0) ? (
              <>

                {/*<p>{legendWidth}</p>*/}

                { filter?.filterType === filterSingleDay &&
                    weatherSingleDay && chartOptions && Object.keys(weatherSingleDay).length > 0 &&
                    <Table sx={{
                      width: 'calc(100% - 30px)',
                      marginLeft: '20px',
                      tableLayout: 'fixed'

                    }}

                    >
                      <TableHead>
                        {/*<TableRow>*/}
                        {(chartOptions?.series[0]?.data || []).map((serie, index) => {
                          return (
                              <TableCell key={index}>
                                <Box sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "0px",
                                  margin: "0px",
                                }}>
                                  {getImage(index)}

                                </Box>

                              </TableCell>
                          );
                        })}
                      </TableHead>
                    </Table>
                }

                { filter?.filterType === filterWeek && weatherDays && sumUpDays && sumUpDays.length > 0 &&

                    <Table sx={{
                      // width: legendWidth ? `calc(100% - ${legendWidth + 40}px)` : 'calc(100% - 30px)',
                      width: 'calc(100% - 30px)',
                      marginLeft: '20px',
                      tableLayout: 'fixed'
                    }}>
                      <TableHead>
                        {/*<TableRow>*/}
                        {(sumUpDays).map((sumUpDay, index) => {
                          return (
                              <>
                                <TableCell key={index}>
                                  <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "0px",
                                    margin: "0px",
                                  }}>
                                    {getWeatherImageDay(sumUpDay.id)}
                                  </Box>
                                </TableCell>
                              </>
                          );
                        })}
                      </TableHead>
                    </Table>


                }
                {chartOptions && (
                    <Chart
                        height={320}
                        options={chartOptions.options}
                        series={chartOptions.series}
                        type="bar"
                    />
                )}
              </>
          ) : (
              <NoData />
          )}
        </CardContent>
      </Card>
  );
}

export default BarTurnover;
